import Dexie from 'dexie';

// Offline First, Multi User, Multi Device, Server Synced Gelesenen Content Tracker
class ContentTracker {
    constructor(nutzer) {
        if (ContentTracker.instance) {
            return ContentTracker.instance;
        }

        ContentTracker.instance = this;

        this.db = new Dexie('InhaltGelesen');

        this.db.version(1).stores({
            contents: '[nutzer+id], type, gelesenStatus, creaDate, updateDate, syncStatus',
        });

        this.sync = null;

        this.nutzer = nutzer;

        this.apiEndpoint = process.env.API_URL + '/inhaltgelesen';

        return this;
    }

    /**
     * 
     * @returns {ContentTracker}
     */
    static getInstance() {
        if (!ContentTracker.instance) {
            ContentTracker.instance = new ContentTracker((typeof IwwUserdata !== 'undefined' && IwwUserdata.USERDATA["id"] !== "" && typeof IwwUserdata.USERDATA["id"] !== 'undefined') ? IwwUserdata.USERDATA["id"] : "unknown");
        }

        return ContentTracker.instance;
    }

    // Status eines Elements setzen
    async setContentStatus(id, type, gelesenStatus) {
        const now = Math.floor(Date.now() / 1000);
        try {
            let localItem = await this.db.contents.get([this.nutzer, id]);
            if (localItem) {
                await this.db.contents.update([this.nutzer, id], { gelesenStatus: gelesenStatus, updateDate: now, syncStatus: 'pending' });
            } else {
                await this.db.contents.add({
                    nutzer: this.nutzer,
                    id: id,
                    type: type,
                    gelesenStatus: gelesenStatus,
                    updateDate: now,
                    creaDate: now,
                    syncStatus: 'pending'
                });
            }

            if (this.nutzer !== "unknown") {
                await this.triggerSync();
            }

            return true;
        } catch (error) {
            console.error('Error setting content status:', error);
            return false;
        }
    }

    async triggerSync() {
        if (this.sync) {
            await this.sync;
            return;
        }

        this.sync = (async () => {
            try {
                await this.syncWithServer();
            } finally {
                this.sync = null;
            }
        })();

        await this.sync;
    }

    // Synchronisation mit dem Server
    async syncWithServer() {
        try {
            if (this.nutzer == "unknown") {
                console.log('[GelesenSync] Not logged in');
                return;
            }

            // Hole alle nicht synchronisierten Einträge
            const pendingItems = await this.db.contents
                .where('syncStatus')
                .equals('pending')
                .toArray();

            console.log("Syncing " + pendingItems.length + " items to server");


            if (pendingItems.length === 0 && this.nutzer !== "unknown") {
                const response = await fetch(this.apiEndpoint + '/sync', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ "changes": [] })
                });

                if (!response.ok) {
                    throw new Error('Sync failed');
                }

                const serverData = await response.json();

                // Server-Änderungen verarbeiten (Latest Wins)
                await this.processServerChanges(serverData);
            } else if (this.nutzer !== "unknown") {
                // Check ob Duplikat (Kann auftreten wenn Artikel Gelesen (nicht angemeldet) und Artikel Gelesen (angemeldet))
                let unknownUserItems = pendingItems.filter(item => item.nutzer == "unknown");

                // Async NutzerID Updaten oder Duplicat Entfernen aus indexedDB
                unknownUserItems.forEach(async (item, index) => {
                    let duplicat = pendingItems.find(inhalt => inhalt.id == item.id && inhalt.updateDate > item.updateDate);
                    await this.db.contents.delete(["unknown", item.id]);

                    let normalDuplicat = await this.db.contents.get([this.nutzer, item.id]);

                    if (!duplicat && !normalDuplicat) {
                        item.nutzer = this.nutzer;
                        await this.db.contents.add(item);
                    }
                });

                // Aktueller Datensatz NutzerID Updaten oder Duplicat Entfernen
                unknownUserItems.forEach((item, index) => {
                    let duplicat = pendingItems.find(inhalt => inhalt.id == item.id && inhalt.updateDate > item.updateDate);

                    if (duplicat) {
                        pendingItems.splice(index, 1);
                    } else {
                        pendingItems[index].nutzer = this.nutzer;
                    }
                });

                const response = await fetch(this.apiEndpoint + '/sync', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ "changes": pendingItems })
                });

                if (!response.ok) {
                    throw new Error('Sync failed');
                }

                const serverData = await response.json();

                // Server-Änderungen verarbeiten (Latest Wins)
                await this.processServerChanges(serverData);

                // Erfolgreiche Sync markieren
                await Promise.all(serverData.map(item =>
                    this.db.contents.update(
                        [this.nutzer, item.id],
                        { syncStatus: 'synced' }
                    )
                ));
            }
        } catch (error) {
            console.error('[Sync Inhalt]', error);
        }
    }

    // Verarbeitung der Server-Änderungen
    async processServerChanges(serverData) {
        console.log("Syncing " + serverData.length + " items from server");
        for (const serverItem of serverData) {
            const localItem = await this.db.contents.get([
                this.nutzer, serverItem.id
            ]);

            let uuidRegex = new RegExp("^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$", "i");
            if (!uuidRegex.test(serverItem.id)) {
                if (localItem) {
                    await this.db.contents.delete([this.nutzer, serverItem.id]);
                }
                continue;
            }

            // Inhalts Eintrag Existiert nicht oder Server änderungen sind neuer
            if (!localItem) {
                await this.db.contents.add({
                    nutzer: this.nutzer,
                    id: serverItem.id,
                    type: serverItem.type,
                    gelesenStatus: serverItem.gelesenStatus,
                    updateDate: serverItem.updateDate,
                    creaDate: serverItem.creaDate,
                    syncStatus: 'synced'
                });
            } else if (serverItem.updateDate > localItem.updateDate) {
                await this.db.contents.update([this.nutzer, localItem.id], {
                    gelesenStatus: serverItem.gelesenStatus,
                    updateDate: serverItem.updateDate,
                    syncStatus: 'synced'
                });
            }
        }
    }

    // Status eines Elements abrufen
    async getContentStatus(id) {
        try {
            const content = await this.db.contents.get([this.nutzer, id]);
            return content?.gelesenStatus ?? false;
        } catch (error) {
            console.error('Error getting content status:', error);
            return false;
        }
    }

    async getContent(id) {
        try {
            const content = await this.db.contents.get([this.nutzer, id]);
            return content ?? false;
        } catch (error) {
            console.error('Error getting content:', error);
            return false;
        }
    }

    async getContentIDsReadByType(type) {
        try {
            let contents = await this.db.contents.where('type').equals(type).toArray();

            let neusteContents = contents.reduce((map, obj) => {
                let uuidRegex = new RegExp("^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$", "i");
                if (!uuidRegex.test(obj.id)) {
                    return map;
                }

                const existing = map.get(obj.id);

                if (!existing || obj.updateDate > existing.updateDate) {
                    map.set(obj.id, obj);
                }

                return map;
            }, new Map());

            let neusteGeleseneContents = Array.from(neusteContents.values()).filter(obj => obj.gelesenStatus == true).map(obj => obj.id);

            return neusteGeleseneContents;
        } catch (error) {
            console.error('Error getting content by type:'.type, error);
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    let contentTracker = ContentTracker.getInstance();
    contentTracker.triggerSync();
});

export default ContentTracker.getInstance();